import {
  ArrowLeftIcon, ArrowRightIcon, Cross1Icon, DownloadIcon,
} from '@radix-ui/react-icons';
import { useEffect, useRef, useState } from 'react';
import {
  CarouselContainer,
  DeleteButton,
  DownloadButton,
  ImageWrapper,
  LabelCountImages,
  MainImage,
  NavButton,
  ScrollThumb,
  ScrollTrack,
  Thumbnail,
  Thumbnails,
  ThumbnailsWrapper,
} from './style';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';

type CarouselProps = {
  readonly images: string[];
  readonly showViewMode?: boolean;
  readonly onDelete?: (index: number) => void;
  readonly showCount?: boolean;
};

export default function NewCarousel({
  images, showViewMode = false, onDelete, showCount = true,
}: CarouselProps): JSX.Element {
  const [current, setCurrent] = useState(0);
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [scrollData, setScrollData] = useState({ width: 0, left: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartLeft = useRef(0);

  const updateThumb = () => {
    if (!ref.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = ref.current;
    const width = (clientWidth / scrollWidth) * 100;
    const left = (scrollLeft / scrollWidth) * 100;
    setScrollData({ width, left });
  };

  const handleScroll = () => updateThumb();

  const handleMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    dragStartX.current = e.clientX;
    dragStartLeft.current = scrollData.left;
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging || !ref.current) return;
    const { scrollWidth, clientWidth } = ref.current;

    const deltaX = e.clientX - dragStartX.current;
    const trackWidth = clientWidth;
    const newLeftPercent = dragStartLeft.current + (deltaX / trackWidth) * 100;

    const clamped = Math.max(0, Math.min(100 - scrollData.width, newLeftPercent));

    const newScrollLeft = (clamped / 100) * scrollWidth;
    ref.current.scrollLeft = newScrollLeft;

    updateThumb();
  };

  const handleMouseUp = () => setDragging(false);

  useEffect(() => {
    if (images.length === 0) {
      setCurrent(0);
    } else if (current >= images.length) {
      setCurrent(images.length - 1);
    }
  }, [images, current]);

  useEffect(() => {
    if (!ref.current || images.length === 0) return;

    const container = ref.current;
    const selectedThumb = container.children[current] as HTMLElement;
    if (!selectedThumb) return;

    const containerRect = container.getBoundingClientRect();
    const thumbRect = selectedThumb.getBoundingClientRect();

    if (thumbRect.left < containerRect.left) {
      container.scrollLeft -= containerRect.left - thumbRect.left;
    } else if (thumbRect.right > containerRect.right) {
      container.scrollLeft += thumbRect.right - containerRect.right;
    }

    updateThumb();
  }, [current, images]);

  useEffect(() => {
    updateThumb();
  }, [images]);

  useEffect(() => {
    if (!dragging) return;

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);

  const nextImage = () => {
    setCurrent((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrent((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <>
      {showCount ? (
        <Flex style={{ width: '100%' }}>
          <LabelCountImages>
            {`${images.length} ${t('imagensAdicionadas')}`}
          </LabelCountImages>
        </Flex>
      ) : null}
      <CarouselContainer>
        <ImageWrapper>
          <MainImage src={images[current]} alt={`Slide ${current}`} />
          <DownloadButton href={images[current]} download={`image-${current}.png`}>
            <DownloadIcon width={20} height={16} style={{ marginRight: '10px' }} />
            {t('baixarImagem')}
          </DownloadButton>

          <NavButton position="left" onClick={prevImage}>
            <ArrowLeftIcon />
          </NavButton>
          <NavButton position="right" onClick={nextImage}>
            <ArrowRightIcon />
          </NavButton>

          {showViewMode && (
            <DeleteButton onClick={() => onDelete?.(current)}>
              <Cross1Icon />
            </DeleteButton>
          )}
        </ImageWrapper>

        <ThumbnailsWrapper>
          <Thumbnails ref={ref} onScroll={handleScroll}>
            {images.map((img, index) => (
              <Thumbnail
                key={index}
                src={img}
                alt={`Thumb ${index}`}
                active={current === index}
                onClick={() => setCurrent(index)}
              />
            ))}
          </Thumbnails>
          <ScrollTrack />
          <ScrollThumb
            width={scrollData.width}
            left={scrollData.left}
            onMouseDown={handleMouseDown}
          />
        </ThumbnailsWrapper>
      </CarouselContainer>
    </>
  );
}
